import React, { useEffect, useState } from 'react';
import MediaBox from './MediaBox';
import { resolveMediaUrl } from '../utils/mediaApi';

export type ChatAttachmentData = {
  attachmentURL?: string | null;
  attachmentType?: string | null;
  attachmentName?: string | null;
  attachmentSize?: number | null;
  durationSec?: number | null;
};

export function formatBytes(n?: number | null) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function useResolved(key?: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!key) return;
    let active = true;
    resolveMediaUrl(key)
      .then((u) => { if (active) setUrl(u); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [key]);
  return { url, failed };
}

const VoiceNote: React.FC<{ objectKey: string; mine: boolean }> = ({ objectKey, mine }) => {
  const { url, failed } = useResolved(objectKey);
  if (failed) return <span style={{ fontSize: 13, opacity: 0.8 }}>Voice message unavailable</span>;
  if (!url) return <span style={{ fontSize: 13, opacity: 0.8 }}>🎤 Loading voice message…</span>;
  return (
    <audio
      controls
      preload="metadata"
      src={url}
      style={{ width: 240, maxWidth: '100%', height: 40, display: 'block', filter: mine ? 'invert(1) hue-rotate(180deg)' : undefined }}
    />
  );
};

const FileCard: React.FC<{ objectKey: string; name?: string | null; size?: number | null; mine: boolean }> = ({ objectKey, name, size, mine }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const url = await resolveMediaUrl(objectKey, { bypassCache: true });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert('Could not open this file. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={open}
      disabled={busy}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: 240, maxWidth: '100%', padding: '8px 10px',
        borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
        background: mine ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.06)', color: 'inherit',
      }}
    >
      <span style={{ fontSize: 26, flexShrink: 0 }}>📄</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'File'}</span>
        <span style={{ display: 'block', fontSize: 12, opacity: 0.75 }}>{busy ? 'Opening…' : `${formatBytes(size)}${size ? ' · ' : ''}Tap to open`}</span>
      </span>
    </button>
  );
};

const ChatAttachment: React.FC<{ data: ChatAttachmentData; mine: boolean }> = ({ data, mine }) => {
  const key = data.attachmentURL;
  if (!key) return null;
  switch (data.attachmentType) {
    case 'image':
      return <div style={{ width: 240, maxWidth: '100%', borderRadius: 10, overflow: 'hidden' }}><MediaBox src={key} type="image" contain maxHeight={320} /></div>;
    case 'video':
      return <div style={{ width: 240, maxWidth: '100%', borderRadius: 10, overflow: 'hidden' }}><MediaBox src={key} type="video" controls contain maxHeight={360} /></div>;
    case 'audio':
      return <VoiceNote objectKey={key} mine={mine} />;
    default:
      return <FileCard objectKey={key} name={data.attachmentName} size={data.attachmentSize} mine={mine} />;
  }
};

export default ChatAttachment;
